/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { Log } from "../../logging/Log";
import type { IEasPreemptablePlayback, TEasResumeSnapshot } from "./types";

/**
 * Holds what an Emergency Alert System (EAS) alert displaced, so the state can
 * be put back when the alert ends.
 *
 * In-memory for the lifetime of the alert and no longer: a snapshot is only
 * ever read by the restore that follows in the same session, so persisting it
 * would add a storage key and a staleness policy for no gain. An app reload
 * during an alert loses the snapshot, and the restore then has nothing to do —
 * which is correct, because a reload has already torn down the playback the
 * snapshot described.
 *
 * Write side only. Capturing, reading and clearing live here; deciding *when*
 * to capture belongs to `EamModelImpl`, and consuming the snapshot to put
 * playback back belongs to the restore path.
 */
export class EasResumeState {
  private static readonly TAG: string = "EasResumeState";

  private static mInstance: EasResumeState | null = null;

  /** The snapshot, or `null` when nothing is held. */
  private mSnapshot: TEasResumeSnapshot | null = null;

  /**
   * Snapshot the current playback and video-window state.
   *
   * Must be called before anything about the alert touches the player: the
   * three load-bearing fields all describe state the preemption is about to
   * overwrite.
   *
   * Overwrites any snapshot already held. Callers must therefore capture only
   * for the alert that first takes the screen — capturing again when a higher
   * priority alert supersedes a lower one would record the already-preempted
   * state and lose the content the viewer was actually watching.
   *
   * Takes the controller rather than reaching for it: a value import of
   * `VideoPlaybackController` from this package closes a require cycle. See
   * `IEasPreemptablePlayback`.
   *
   * @param playback - Playback controller to read
   * @returns The snapshot that was stored
   */
  public capture(playback: IEasPreemptablePlayback): TEasResumeSnapshot {
    const playable = playback.getPlayable();

    const snapshot: TEasResumeSnapshot = {
      capturedAtMs: Date.now(),
      channelNumber: playback.getCurrentChannelNumber(),
      playbackContentType:
        playable !== null ? playable.playbackContentType : null,
      positionSec: playback.getPlayerCurrentTime(),
      wasForcePaused: playback.isForcePausedByUser(),
      wasMuted: playback.isMuted(),
      wasPlaying: playback.isPlaying()
    };

    this.mSnapshot = snapshot;

    Log.i(
      EasResumeState.TAG,
      `capture(): contentType=${String(snapshot.playbackContentType)}, ` +
        `channel=${snapshot.channelNumber}, ` +
        `positionSec=${String(Math.round(snapshot.positionSec))}, ` +
        `wasPlaying=${String(snapshot.wasPlaying)}, ` +
        `wasForcePaused=${String(snapshot.wasForcePaused)}, ` +
        `wasMuted=${String(snapshot.wasMuted)}`
    );

    return snapshot;
  }

  /**
   * The held snapshot, or `null` when nothing was captured.
   *
   * @returns The snapshot
   */
  public get(): TEasResumeSnapshot | null {
    return this.mSnapshot;
  }

  /**
   * Drop the held snapshot. Idempotent.
   *
   * Called by the restore path once the state has been put back, so a later
   * alert cannot restore stale content.
   */
  public clear(): void {
    if (this.mSnapshot === null) {
      return;
    }
    this.mSnapshot = null;
    Log.d(EasResumeState.TAG, "clear(): snapshot dropped");
  }

  /**
   * Get the shared instance, creating it on first use.
   *
   * @returns The singleton instance
   */
  public static getInstance(): EasResumeState {
    if (EasResumeState.mInstance === null) {
      EasResumeState.mInstance = new EasResumeState();
    }
    return EasResumeState.mInstance;
  }

  /**
   * Discard the shared instance so the next getInstance() builds a fresh one.
   * Test-only.
   */
  public static __resetForTest(): void {
    EasResumeState.mInstance = null;
  }
}
